import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { makeMasyu, masyuWin } from "@/lib/masyu";

export function MasyuGame({ n, onWin, resetKey }) {
  const { t } = useTranslation();
  const [puzzle, setPuzzle] = useState(null);
  const [edges, setEdges] = useState(new Set());

  const start = useCallback(() => {
    setPuzzle(makeMasyu(n));
    setEdges(new Set());
  }, [n]);

  useEffect(() => { start(); }, [start, resetKey]);

  const edgesArr = useMemo(() => [...edges], [edges]);
  const solved = useMemo(() => (puzzle ? masyuWin(edgesArr, puzzle) : false), [edgesArr, puzzle]);
  useEffect(() => { if (solved) onWin?.(); }, [solved, onWin]);

  const toggle = (key) => {
    if (solved) return;
    setEdges((prev) => {
      const ns = new Set(prev);
      if (ns.has(key)) ns.delete(key); else ns.add(key);
      return ns;
    });
  };

  if (!puzzle) return null;
  const pearlAt = {};
  puzzle.pearls.forEach((p) => { pearlAt[`${p.r},${p.c}`] = p.type; });
  const B = "min(88vw, 440px)";
  const pct = (v) => `${(v * 100) / n}%`;
  const dot = `calc(${B} / ${n} * 0.5)`;

  const hEdges = [];
  for (let r = 0; r < n; r++) for (let c = 0; c < n - 1; c++) hEdges.push({ key: `H:${r}:${c}`, r, c });
  const vEdges = [];
  for (let r = 0; r < n - 1; r++) for (let c = 0; c < n; c++) vEdges.push({ key: `V:${r}:${c}`, r, c });

  return (
    <div className="flex flex-col items-center" data-testid="masyu-game">
      <p className="text-xs text-foreground/45 text-center max-w-md mb-6 leading-relaxed">{t("games.masyuRules")}</p>
      <div className="relative rounded-xl border border-white/10 bg-white/[0.02]" style={{ width: B, height: B }} data-testid="masyu-board">
        {/* active edge lines */}
        {edgesArr.map((key) => {
          const [o, a, b] = key.split(":").map((x, i) => (i === 0 ? x : parseInt(x)));
          if (o === "H") return <span key={key} className="absolute bg-primary z-10" style={{ left: pct(b + 0.5), top: pct(a + 0.5), width: pct(1), height: "3px", transform: "translateY(-50%)" }} />;
          return <span key={key} className="absolute bg-primary z-10" style={{ left: pct(b + 0.5), top: pct(a + 0.5), width: "3px", height: pct(1), transform: "translateX(-50%)" }} />;
        })}
        {/* clickable edges */}
        {hEdges.map((e) => (
          <button key={e.key} data-testid={`masyu-h-${e.r}-${e.c}`} onClick={() => toggle(e.key)}
            style={{ position: "absolute", left: pct(e.c + 1), top: pct(e.r + 0.5), width: pct(1), height: `calc(${B}/${n}*0.5)`, transform: "translate(-50%,-50%)" }} className="z-20" />
        ))}
        {vEdges.map((e) => (
          <button key={e.key} data-testid={`masyu-v-${e.r}-${e.c}`} onClick={() => toggle(e.key)}
            style={{ position: "absolute", left: pct(e.c + 0.5), top: pct(e.r + 1), width: `calc(${B}/${n}*0.5)`, height: pct(1), transform: "translate(-50%,-50%)" }} className="z-20" />
        ))}
        {/* pearls / dots */}
        {Array.from({ length: n }, (_, r) =>
          Array.from({ length: n }, (_, c) => {
            const type = pearlAt[`${r},${c}`];
            return (
              <div key={`${r}-${c}`} data-testid={`masyu-cell-${r}-${c}`}
                style={{ position: "absolute", left: pct(c + 0.5), top: pct(r + 0.5), transform: "translate(-50%,-50%)", width: dot, height: dot }}
                className="z-30 pointer-events-none flex items-center justify-center">
                {type === "W" ? (
                  <span className="rounded-full bg-background border-2 border-foreground/80" style={{ width: "100%", height: "100%" }} />
                ) : type === "B" ? (
                  <span className="rounded-full bg-foreground" style={{ width: "100%", height: "100%" }} />
                ) : (
                  <span className="rounded-full bg-foreground/20" style={{ width: "22%", height: "22%" }} />
                )}
              </div>
            );
          })
        )}
      </div>
      <button onClick={start} data-testid="masyu-new-btn" className="mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/45 hover:text-primary transition-colors">
        <RefreshCw size={13} /> {t("games.newGrid")}
      </button>
    </div>
  );
}
